import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { loadVehicleTypes, getIconStyle, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { formatDate } from "@/lib/dates";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  CalendarDays,
  Car,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock3,
  Truck,
  Sun,
  Moon,
  Coffee,
  Users,
  Timer,
  AlertCircle,
  RefreshCw,
  Calendar,
  Eye,
  EyeOff,
  Info,
  CalendarOff,
  LayoutGrid,
  CalendarRange,
  ArrowLeftRight,
  Send,
  CheckCircle2,
  XCircle,
  Hourglass,
  Gavel,
  PlusCircle,
} from "lucide-react";
import { normalizeHHMM, formatTime12, formatDuration } from "@/components/roster/utils";
import TimeOffRequestDialog from "@/components/roster/TimeOffRequestDialog";
import type { TimeOffRequest } from "@/components/roster/TimeOffRequestDialog";

type ShiftRow = {
  id: string;
  driver_name: string;
  driver_firstname?: string | null;
  driver_lastname?: string | null;
  vehicle_number?: string | null;
  vehicle_type?: string | null;
  transmission_type?: string | null;
  shift_type_code: string;
  shift_type_label: string;
  shift_type_description?: string | null;
  shift_type_default_colour_hex?: string | null;
  allowed_vehicle_types: string[];
  requires_sts: boolean;
  requires_tmv: boolean;
  can_be_open_shift: boolean;
  counts_as_worked_hours: boolean;
  status:
    | "draft"
    | "published"
    | "available"
    | "claimed"
    | "assigned"
    | "completed"
    | "cancelled";
  start_at: string;
  end_at: string;
  break_minutes: number;
  driver_visible_notes?: string | null;
  admin_notes?: string | null;
  colour_hex?: string | null;
  shift_date: string;
  start_hhmm: string;
  end_hhmm: string;
  worked_hours: number;
};

type HolidayRow = {
  id: string;
  holiday_name: string;
  holiday_date: string;
  region?: string | null;
  is_national: boolean;
};

type SwapRequest = {
  id: string;
  requesting_driver: string;
  target_driver: string | null;
  shift_id: string;
  target_shift_id: string | null;
  reason: string | null;
  status: "pending" | "approved" | "rejected";
  admin_notes: string | null;
  requested_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  is_open_bid: boolean;
  bid_deadline: string | null;
  selected_bid_id: string | null;
  request_type: "release" | "direct_swap";
};

type SwapBid = {
  id: string;
  swap_request_id: string;
  bidding_driver: string;
  offered_shift_id: string | null;
  note: string | null;
  status: "pending" | "accepted" | "rejected" | "withdrawn";
  created_at: string;
  updated_at: string;
};

type ShiftLookup = {
  id: string;
  driver_name: string;
  shift_date: string;
  start_hhmm: string;
  end_hhmm: string;
  shift_type_label?: string | null;
  shift_type_code?: string | null;
  vehicle_number?: string | null;
};

interface DriverRosterViewProps {
  driverName: string;
}

type DriverCalendarView = "day" | "week" | "month";

const NZ_WEEK_STARTS_ON = 1;

const getShiftColour = (shift: ShiftRow): string => {
  return shift.colour_hex || shift.shift_type_default_colour_hex || "#475569";
};

const getStatusBadgeVariant = (status: ShiftRow["status"]) => {
  switch (status) {
    case "published":
      return "default" as const;
    case "assigned":
      return "default" as const;
    case "available":
      return "outline" as const;
    case "completed":
      return "secondary" as const;
    case "cancelled":
      return "destructive" as const;
    default:
      return "secondary" as const;
  }
};

const getVehicleTypeIcon = (vehicleType: string | null | undefined, vehicleTypes: VehicleTypeRow[]) => {
  if (getIconStyle(vehicleType, vehicleTypes) === "truck") {
    return <Truck className="h-4 w-4" />;
  }
  return <Car className="h-4 w-4" />;
};

const driverDisplayName = (shift: ShiftRow): string => {
  if (shift.driver_firstname && shift.driver_lastname) {
    return `${shift.driver_firstname} ${shift.driver_lastname.charAt(0)}.`;
  }
  return shift.driver_name;
};

const stringToColour = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colours = [
    "#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6",
    "#EC4899", "#06B6D4", "#F97316", "#6366F1", "#14B8A6",
  ];
  return colours[Math.abs(hash) % colours.length];
};

const getInitials = (shift: ShiftRow): string => {
  if (shift.driver_firstname && shift.driver_lastname) {
    return (shift.driver_firstname[0] + shift.driver_lastname[0]).toUpperCase();
  }
  const parts = shift.driver_name.split(/[\s_-]+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return shift.driver_name.substring(0, 2).toUpperCase();
};

const getShiftPeriod = (hhmm: string): "morning" | "afternoon" | "night" => {
  const normalised = normalizeHHMM(hhmm);
  const h = parseInt(normalised.split(":")[0], 10);
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "night";
};

const getShiftPeriodIcon = (hhmm: string) => {
  const period = getShiftPeriod(hhmm);
  if (period === "morning") return <Sun className="h-3.5 w-3.5 text-amber-400" />;
  if (period === "afternoon") return <Coffee className="h-3.5 w-3.5 text-orange-400" />;
  return <Moon className="h-3.5 w-3.5 text-indigo-400" />;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_WIDTH = 80;
const TIMELINE_WIDTH = HOURS.length * HOUR_WIDTH;
const DRIVER_COL_WIDTH = 200;
const ROW_HEIGHT = 52;
const ROW_GAP = 4;
const HEADER_HEIGHT = 36;

const formatHour = (h: number): string => {
  if (h === 0) return "12AM";
  if (h < 12) return `${h}AM`;
  if (h === 12) return "12PM";
  return `${h - 12}PM`;
};

const timeToHours = (hhmm: string): number => {
  const normalised = normalizeHHMM(hhmm);
  const [h, m] = normalised.split(":").map(Number);
  return h + m / 60;
};

const formatShiftSummary = (shift?: ShiftLookup | null) => {
  if (!shift) return "Shift details unavailable";
  const shiftDate = new Date(`${shift.shift_date}T00:00:00`);
  return `${format(shiftDate, "EEE")} ${formatDate(shiftDate)} · ${shift.start_hhmm}-${shift.end_hhmm}${shift.shift_type_label ? ` · ${shift.shift_type_label}` : ""}${shift.vehicle_number ? ` · ${shift.vehicle_number}` : ""}`;
};

const DriverRosterView: React.FC<DriverRosterViewProps> = ({ driverName }) => {
  const [anchorDate, setAnchorDate] = useState<Date>(new Date());
  const [calendarView, setCalendarView] = useState<DriverCalendarView>("week");
  const [loading, setLoading] = useState<boolean>(false);
  const [myShifts, setMyShifts] = useState<ShiftRow[]>([]);
  const [fullSchedule, setFullSchedule] = useState<ShiftRow[]>([]);
  const [holidays, setHolidays] = useState<HolidayRow[]>([]);
  const [scheduledBreaksByShiftId, setScheduledBreaksByShiftId] = useState<
    Record<string, { start: string; end: string }>
  >({});
  const [activeTab, setActiveTab] = useState("my-shifts");
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [showOtherDriverNames, setShowOtherDriverNames] = useState(true);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);

  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [timeOffDialogOpen, setTimeOffDialogOpen] = useState(false);
  const [timeOffDefaultDate, setTimeOffDefaultDate] = useState<string | undefined>(undefined);

  const [swapRequests, setSwapRequests] = useState<SwapRequest[]>([]);
  const [openBidRequests, setOpenBidRequests] = useState<SwapRequest[]>([]);
  const [myBids, setMyBids] = useState<SwapBid[]>([]);
  const [swapShiftMap, setSwapShiftMap] = useState<Record<string, ShiftLookup>>({});

  const [swapLoading, setSwapLoading] = useState(false);
  const [creatingSwap, setCreatingSwap] = useState(false);
  const [placingBidId, setPlacingBidId] = useState<string | null>(null);

  const [selectedOwnShiftId, setSelectedOwnShiftId] = useState("");
  const [selectedTargetShiftId, setSelectedTargetShiftId] = useState("");
  const [swapReason, setSwapReason] = useState("");
  const [swapMode, setSwapMode] = useState<"open_bid" | "direct_swap">("open_bid");

  const [bidNotes, setBidNotes] = useState<Record<string, string>>({});

  const visibleStatuses = ["published", "assigned", "available", "completed"];

  const { rangeStart, rangeEnd, rangeDays } = useMemo(() => {
    if (calendarView === "day") {
      return {
        rangeStart: anchorDate,
        rangeEnd: anchorDate,
        rangeDays: [anchorDate],
      };
    }

    if (calendarView === "week") {
      const ws = startOfWeek(anchorDate, { weekStartsOn: NZ_WEEK_STARTS_ON });
      const we = endOfWeek(anchorDate, { weekStartsOn: NZ_WEEK_STARTS_ON });
      const days: Date[] = Array.from({ length: 7 }).map((_, i) => addDays(ws, i));
      return { rangeStart: ws, rangeEnd: we, rangeDays: days };
    }

    const ms = startOfMonth(anchorDate);
    const me = endOfMonth(anchorDate);
    const calStart = startOfWeek(ms, { weekStartsOn: NZ_WEEK_STARTS_ON });
    const calEnd = endOfWeek(me, { weekStartsOn: NZ_WEEK_STARTS_ON });
    const days: Date[] = [];
    let d = calStart;
    while (d <= calEnd) {
      days.push(d);
      d = addDays(d, 1);
    }
    return { rangeStart: calStart, rangeEnd: calEnd, rangeDays: days };
  }, [anchorDate, calendarView]);

  const rangeStartString = format(rangeStart, "yyyy-MM-dd");
  const rangeEndString = format(rangeEnd, "yyyy-MM-dd");

  const headerLabel = useMemo(() => {
    if (calendarView === "day") return `${format(anchorDate, "EEEE")}, ${formatDate(anchorDate)}`;
    if (calendarView === "week") {
      const ws = startOfWeek(anchorDate, { weekStartsOn: NZ_WEEK_STARTS_ON });
      const we = endOfWeek(anchorDate, { weekStartsOn: NZ_WEEK_STARTS_ON });
      return `${formatDate(ws)} – ${formatDate(we)}`;
    }
    // Spelled-out month/year, not formatDate -- see ActualPerformanceTab.tsx.
    return format(anchorDate, "MMMM yyyy");
  }, [anchorDate, calendarView]);

  const loadRoster = useCallback(async () => {
    setLoading(true);

    try {
      const [
        myShiftsRes,
        fullScheduleRes,
        holidaysRes,
        timeOffRes,
        myRequestsRes,
        openRequestsRes,
        myBidsRes,
        scheduledBreaksRes,
      ] = await Promise.all([
        supabase
          .from("vw_roster_shifts_enriched")
          .select("*")
          .eq("driver_name", driverName)
          .in("status", visibleStatuses)
          .gte("shift_date", rangeStartString)
          .lte("shift_date", rangeEndString)
          .order("start_at", { ascending: true }),

        supabase
          .from("vw_roster_shifts_enriched")
          .select("*")
          .in("status", visibleStatuses)
          .gte("shift_date", rangeStartString)
          .lte("shift_date", rangeEndString)
          .order("start_at", { ascending: true }),

        supabase
          .from("nz_holidays")
          .select("*")
          .gte("holiday_date", rangeStartString)
          .lte("holiday_date", rangeEndString)
          .order("holiday_date", { ascending: true }),

        supabase
          .from("roster_time_off_requests")
          .select("*")
          .eq("driver_name", driverName)
          .order("requested_at", { ascending: false })
          .limit(20),

        supabase
          .from("roster_swap_requests")
          .select("*")
          .eq("requesting_driver", driverName)
          .order("requested_at", { ascending: false })
          .limit(50),

        supabase
          .from("roster_swap_requests")
          .select("*")
          .eq("is_open_bid", true)
          .eq("status", "pending")
          .neq("requesting_driver", driverName)
          .order("requested_at", { ascending: false })
          .limit(50),

        supabase
          .from("roster_swap_bids")
          .select("*")
          .eq("bidding_driver", driverName)
          .order("created_at", { ascending: false })
          .limit(50),

        supabase
          .from("roster_scheduled_breaks")
          .select("shift_id, suggested_start_hhmm, suggested_end_hhmm, confirmed_start_hhmm, confirmed_end_hhmm")
          .eq("driver_name", driverName)
          .eq("status", "published")
          .gte("break_date", rangeStartString)
          .lte("break_date", rangeEndString),
      ]);

      const errors = [
        myShiftsRes.error,
        fullScheduleRes.error,
        holidaysRes.error,
        timeOffRes.error,
        myRequestsRes.error,
        openRequestsRes.error,
        myBidsRes.error,
        scheduledBreaksRes.error,
      ].filter(Boolean);

      if (errors.length > 0) {
        throw new Error(errors.map((e) => e?.message).join(" | "));
      }

      const myShiftsData = (myShiftsRes.data ?? []) as ShiftRow[];
      const fullScheduleData = (fullScheduleRes.data ?? []) as ShiftRow[];
      const myRequestsData = (myRequestsRes.data ?? []) as SwapRequest[];
      const openRequestsData = (openRequestsRes.data ?? []) as SwapRequest[];
      const myBidsData = (myBidsRes.data ?? []) as SwapBid[];

      setMyShifts(myShiftsData);
      setFullSchedule(fullScheduleData);
      setHolidays((holidaysRes.data ?? []) as HolidayRow[]);
      setTimeOffRequests((timeOffRes.data ?? []) as TimeOffRequest[]);
      setSwapRequests(myRequestsData);
      setOpenBidRequests(openRequestsData);
      setMyBids(myBidsData);

      const nextBreaksByShiftId: Record<string, { start: string; end: string }> = {};
      for (const row of scheduledBreaksRes.data ?? []) {
        const start = row.confirmed_start_hhmm || row.suggested_start_hhmm;
        const end = row.confirmed_end_hhmm || row.suggested_end_hhmm;
        if (start && end) nextBreaksByShiftId[row.shift_id] = { start, end };
      }
      setScheduledBreaksByShiftId(nextBreaksByShiftId);

      const shiftIds = new Set<string>();

      for (const shift of fullScheduleData) {
        shiftIds.add(shift.id);
      }

      for (const req of [...myRequestsData, ...openRequestsData]) {
        if (req.shift_id) shiftIds.add(req.shift_id);
        if (req.target_shift_id) shiftIds.add(req.target_shift_id);
      }

      for (const bid of myBidsData) {
        if (bid.offered_shift_id) shiftIds.add(bid.offered_shift_id);
      }

      if (shiftIds.size > 0) {
        const { data: shiftLookupData, error: shiftLookupError } = await supabase
          .from("vw_roster_shifts_enriched")
          .select("id, driver_name, shift_date, start_hhmm, end_hhmm, shift_type_label, shift_type_code, vehicle_number")
          .in("id", Array.from(shiftIds));

        if (shiftLookupError) throw shiftLookupError;

        const nextShiftMap: Record<string, ShiftLookup> = {};
        for (const shift of (shiftLookupData ?? []) as ShiftLookup[]) {
          nextShiftMap[shift.id] = shift;
        }
        setSwapShiftMap(nextShiftMap);
      } else {
        setSwapShiftMap({});
      }
    } catch (error) {
      console.error("Failed to load driver roster:", error);
    } finally {
      setLoading(false);
    }
  }, [driverName, rangeStartString, rangeEndString]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const goBack = () => {
    if (calendarView === "day") setAnchorDate((prev) => addDays(prev, -1));
    else if (calendarView === "week") setAnchorDate((prev) => addDays(prev, -7));
    else setAnchorDate((prev) => addMonths(prev, -1));
    setSelectedDay(null);
  };

  const goForward = () => {
    if (calendarView === "day") setAnchorDate((prev) => addDays(prev, 1));
    else if (calendarView === "week") setAnchorDate((prev) => addDays(prev, 7));
    else setAnchorDate((prev) => addMonths(prev, 1));
    setSelectedDay(null);
  };

  const goToday = () => {
    setAnchorDate(new Date());
    setSelectedDay(null);
  };

  const myShiftsByDate = useMemo(() => {
    const map: Record<string, ShiftRow[]> = {};
    for (const day of rangeDays) {
      map[format(day, "yyyy-MM-dd")] = [];
    }
    for (const shift of myShifts) {
      if (map[shift.shift_date]) map[shift.shift_date].push(shift);
    }
    return map;
  }, [myShifts, rangeDays]);

  const fullScheduleByDate = useMemo(() => {
    const map: Record<string, ShiftRow[]> = {};
    for (const day of rangeDays) {
      map[format(day, "yyyy-MM-dd")] = [];
    }
    for (const shift of fullSchedule) {
      if (map[shift.shift_date]) map[shift.shift_date].push(shift);
    }
    return map;
  }, [fullSchedule, rangeDays]);

  const holidaysByDate = useMemo(() => {
    const map: Record<string, HolidayRow[]> = {};
    for (const h of holidays) {
      if (!map[h.holiday_date]) map[h.holiday_date] = [];
      map[h.holiday_date].push(h);
    }
    return map;
  }, [holidays]);

  const weekStats = useMemo(() => {
    const activeShifts = myShifts.filter((s) => s.status !== "cancelled");
    const totalHours = activeShifts.reduce((sum, s) => sum + (s.worked_hours || 0), 0);
    const totalBreakMinutes = activeShifts.reduce((sum, s) => sum + (s.break_minutes || 0), 0);
    const uniqueVehicles = new Set(activeShifts.filter((s) => s.vehicle_number).map((s) => s.vehicle_number));
    const daysWorking = new Set(activeShifts.map((s) => s.shift_date)).size;

    return {
      totalShifts: activeShifts.length,
      totalHours,
      totalBreakMinutes,
      uniqueVehicles: uniqueVehicles.size,
      daysWorking,
    };
  }, [myShifts]);

  const isToday = (date: Date) => isSameDay(date, new Date());

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const todaysShifts = useMemo(
    () => myShifts.filter((s) => s.shift_date === todayStr && s.status !== "cancelled"),
    [myShifts, todayStr]
  );
  const inTodayRange = todayStr >= rangeStartString && todayStr <= rangeEndString;

  const ownShiftOptions = useMemo(() => {
    return myShifts
      .filter((s) => s.status !== "cancelled" && s.status !== "completed")
      .sort((a, b) => a.shift_date.localeCompare(b.shift_date) || a.start_hhmm.localeCompare(b.start_hhmm));
  }, [myShifts]);

  const targetShiftOptions = useMemo(() => {
    return fullSchedule
      .filter((s) => {
        if (s.id === selectedOwnShiftId) return false;
        if (s.driver_name === driverName) return false;
        if (s.status === "cancelled" || s.status === "completed") return false;
        return true;
      })
      .sort((a, b) => a.shift_date.localeCompare(b.shift_date) || a.start_hhmm.localeCompare(b.start_hhmm));
  }, [fullSchedule, selectedOwnShiftId, driverName]);

  const myBidsByRequestId = useMemo(() => {
    const map = new Map<string, SwapBid>();
    for (const bid of myBids) {
      map.set(bid.swap_request_id, bid);
    }
    return map;
  }, [myBids]);

  const openBidCount = openBidRequests.length;
  const pendingMyBidsCount = myBids.filter((b) => b.status === "pending").length;
  const pendingMyRequestsCount = swapRequests.filter((r) => r.status === "pending").length;

  const renderHolidayBanner = (dateStr: string, compact = false) => {
    const dayHolidays = holidaysByDate[dateStr] || [];
    if (dayHolidays.length === 0) return null;

    return (
      <div className={`bg-orange-500/20 border-b border-orange-500/40 ${compact ? "px-1 py-0.5" : "px-2 py-1"}`}>
        <div className={`${compact ? "text-[9px]" : "text-[10px]"} font-semibold uppercase tracking-wider text-orange-300 truncate`}>
          {dayHolidays.map((h) => h.holiday_name).join(" • ")}
        </div>
      </div>
    );
  };

  const renderShiftCard = (shift: ShiftRow, showDriver = false) => {
    const colour = getShiftColour(shift);
    const isCancelled = shift.status === "cancelled";
    const isMe = shift.driver_name === driverName;

    return (
      <div
        key={shift.id}
        className={`rounded-lg border transition-all ${
          isCancelled
            ? "opacity-40 border-gray-700"
            : isMe
            ? "border-l-4 bg-gray-800/60 hover:bg-gray-800/80"
            : "border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50"
        }`}
        style={{
          borderLeftColor: isMe ? colour : undefined,
          borderLeftWidth: isMe ? "4px" : undefined,
        }}
      >
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {getShiftPeriodIcon(shift.start_hhmm)}
              <span className="text-sm font-semibold text-white">
                {formatTime12(shift.start_hhmm)} – {formatTime12(shift.end_hhmm)}
              </span>
              <span className="text-xs text-gray-400">({formatDuration(shift.worked_hours)})</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0"
                style={{ borderColor: colour, color: colour }}
              >
                {shift.shift_type_label}
              </Badge>
              <Badge variant={getStatusBadgeVariant(shift.status)} className="text-[10px] px-1.5 py-0">
                {shift.status}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-400">
            <div className="flex items-center gap-1">
              {shift.vehicle_number ? (
                <>
                  {getVehicleTypeIcon(shift.vehicle_type, vehicleTypes)}
                  <span className="text-gray-300">{shift.vehicle_number}</span>
                  {shift.transmission_type && (
                    <span className="text-gray-500">({shift.transmission_type})</span>
                  )}
                </>
              ) : (
                <span className="text-yellow-400 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  No vehicle assigned
                </span>
              )}
            </div>

            {scheduledBreaksByShiftId[shift.id] ? (
              <div className="flex items-center gap-1 text-yellow-400">
                <Coffee className="h-3 w-3" />
                <span>
                  Break: {formatTime12(scheduledBreaksByShiftId[shift.id].start)} – {formatTime12(scheduledBreaksByShiftId[shift.id].end)}
                </span>
              </div>
            ) : (
              shift.break_minutes > 0 && (
                <div className="flex items-center gap-1">
                  <Coffee className="h-3 w-3" />
                  <span>{shift.break_minutes}m break</span>
                </div>
              )
            )}

            {showDriver && !isMe && showOtherDriverNames && (
              <div className="flex items-center gap-1 ml-auto">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
                  style={{ backgroundColor: stringToColour(shift.driver_name) }}
                >
                  {getInitials(shift)}
                </div>
                <span className="text-gray-300">{driverDisplayName(shift)}</span>
              </div>
            )}

            {isMe && showDriver && (
              <div className="flex items-center gap-1 ml-auto">
                <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-blue-600">
                  You
                </Badge>
              </div>
            )}
          </div>

          {shift.driver_visible_notes && (
            <div className="mt-2 flex items-start gap-1.5 bg-blue-950/30 border border-blue-800/30 rounded-md px-2.5 py-1.5">
              <Info className="h-3 w-3 text-blue-400 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-300">{shift.driver_visible_notes}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCalendarStrip = (source: Record<string, ShiftRow[]>, allowSelect = true) => (
    <div className={`grid ${calendarView === "month" ? "grid-cols-7" : "grid-cols-7"} gap-2 mb-4`}>
      {rangeDays.map((day) => {
        const dateStr = format(day, "yyyy-MM-dd");
        const dayShifts = source[dateStr] || [];
        const dayHolidays = holidaysByDate[dateStr] || [];
        const today = isToday(day);
        const isSelected = selectedDay === dateStr;
        const hasShifts = dayShifts.length > 0;
        const inMonth = calendarView !== "month" || isSameMonth(day, anchorDate);

        return (
          <button
            key={dateStr}
            onClick={() => allowSelect && setSelectedDay(isSelected ? null : dateStr)}
            className={`rounded-lg border p-2 text-center transition-all ${
              isSelected
                ? "border-blue-500 bg-blue-950/40 ring-2 ring-blue-500/30"
                : dayHolidays.length > 0
                ? "border-orange-500/40 bg-orange-950/15"
                : today
                ? "border-blue-500/50 bg-blue-950/20"
                : hasShifts
                ? "border-gray-600 bg-gray-800/60 hover:border-gray-500"
                : "border-gray-700/50 bg-gray-800/30 hover:border-gray-600"
            } ${!inMonth ? "opacity-50" : ""}`}
          >
            <p className={`text-xs font-medium ${today ? "text-blue-400" : "text-gray-400"}`}>
              {format(day, "EEE")}
            </p>
            <p className={`text-lg font-bold ${
              isSelected ? "text-blue-300" : today ? "text-blue-400" : hasShifts ? "text-white" : "text-gray-500"
            }`}>
              {format(day, "d")}
            </p>
            {dayHolidays.length > 0 && (
              <p className="text-[9px] text-orange-400 truncate mt-0.5" title={dayHolidays[0].holiday_name}>
                {dayHolidays[0].holiday_name}
              </p>
            )}
            {hasShifts ? (
              <div className="flex items-center justify-center gap-0.5 mt-1">
                {dayShifts.slice(0, 4).map((s) => (
                  <div key={s.id} className="w-2 h-2 rounded-full" style={{ backgroundColor: getShiftColour(s) }} />
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-gray-600 mt-1">Off</p>
            )}
          </button>
        );
      })}
    </div>
  );

  const createSwapRequest = async () => {
    if (!selectedOwnShiftId) return;
    setCreatingSwap(true);

    try {
      const isOpenBid = swapMode === "open_bid";
      const targetShift = swapMode === "direct_swap" ? selectedTargetShiftId || null : null;

      const { error } = await supabase.from("roster_swap_requests").insert({
        requesting_driver: driverName,
        target_driver:
          swapMode === "direct_swap"
            ? (targetShiftOptions.find((s) => s.id === targetShift)?.driver_name ?? null)
            : null,
        shift_id: selectedOwnShiftId,
        target_shift_id: targetShift,
        reason: swapReason.trim() || null,
        status: "pending",
        requested_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_open_bid: isOpenBid,
        request_type: isOpenBid ? "release" : "direct_swap",
      });

      if (error) throw error;

      setSelectedOwnShiftId("");
      setSelectedTargetShiftId("");
      setSwapReason("");
      setSwapMode("open_bid");
      await loadRoster();
    } catch (error) {
      console.error("Failed to create swap request:", error);
    } finally {
      setCreatingSwap(false);
    }
  };

  const placeBid = async (requestId: string) => {
    setPlacingBidId(requestId);
    try {
      const existingBid = myBidsByRequestId.get(requestId);
      if (existingBid) return;

      const { error } = await supabase.from("roster_swap_bids").insert({
        swap_request_id: requestId,
        bidding_driver: driverName,
        offered_shift_id: null,
        note: bidNotes[requestId]?.trim() || null,
        status: "pending",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

      if (error) throw error;

      setBidNotes((prev) => ({ ...prev, [requestId]: "" }));
      await loadRoster();
    } catch (error) {
      console.error("Failed to place bid:", error);
    } finally {
      setPlacingBidId(null);
    }
  };

  const renderSwapStatusBadge = (status: SwapRequest["status"]) => {
    if (status === "approved") {
      return (
        <Badge className="bg-green-600 text-white text-[10px]">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Approved
        </Badge>
      );
    }
    if (status === "rejected") {
      return (
        <Badge variant="destructive" className="text-[10px]">
          <XCircle className="h-3 w-3 mr-1" /> Rejected
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="border-amber-500 text-amber-400 text-[10px]">
        <Hourglass className="h-3 w-3 mr-1" /> Pending
      </Badge>
    );
  };

  if (loading && myShifts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-lg text-gray-400">Loading your roster...</span>
      </div>
    );
  }

  const selectedDateStr = selectedDay || format(anchorDate, "yyyy-MM-dd");

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-blue-400" />
            My Roster
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">{headerLabel}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-gray-800 border border-gray-700 rounded-md overflow-hidden">
            <button
              onClick={() => setCalendarView("day")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                calendarView === "day" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <Calendar className="h-3.5 w-3.5" /> Day
            </button>
            <button
              onClick={() => setCalendarView("week")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                calendarView === "week" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <CalendarRange className="h-3.5 w-3.5" /> Week
            </button>
            <button
              onClick={() => setCalendarView("month")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                calendarView === "month" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Month
            </button>
          </div>

          <Button variant="outline" size="sm" onClick={goBack} className="border-gray-600">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} className="border-gray-600">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goForward} className="border-gray-600">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={loadRoster} disabled={loading} className="text-gray-400">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="bg-gray-800/60 border-gray-700">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider">Shifts</p>
              <p className="text-lg font-bold text-white">{weekStats.totalShifts}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/60 border-gray-700">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Timer className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider">Hours</p>
              <p className="text-lg font-bold text-white">{formatDuration(weekStats.totalHours)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/60 border-gray-700">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider">Days</p>
              <p className="text-lg font-bold text-white">{weekStats.daysWorking}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/60 border-gray-700">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Coffee className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider">Breaks</p>
              <p className="text-lg font-bold text-white">{formatDuration(weekStats.totalBreakMinutes / 60)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/60 border-gray-700">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Car className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider">Vehicles</p>
              <p className="text-lg font-bold text-white">{weekStats.uniqueVehicles}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800 border border-gray-700">
          <TabsTrigger value="my-shifts" className="flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            My Shifts
          </TabsTrigger>
          <TabsTrigger value="today-break" className="flex items-center gap-1.5">
            <Coffee className="h-3.5 w-3.5" />
            Today's Break Schedule
          </TabsTrigger>
          <TabsTrigger value="full-schedule" className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            Full Schedule
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-1.5">
            <Clock3 className="h-3.5 w-3.5" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="swaps" className="flex items-center gap-1.5">
            <ArrowLeftRight className="h-3.5 w-3.5" />
            Swaps
            {(pendingMyRequestsCount + pendingMyBidsCount + openBidCount) > 0 && (
              <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-600 px-1 text-[10px] text-white">
                {pendingMyRequestsCount + pendingMyBidsCount + openBidCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="time-off" className="flex items-center gap-1.5">
            <CalendarOff className="h-3.5 w-3.5" />
            Time Off
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-shifts" className="mt-4">
          {renderCalendarStrip(myShiftsByDate, true)}

          {selectedDay ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">
                  {format(new Date(selectedDateStr + "T00:00:00"), "EEEE, d MMMM yyyy")}
                </h3>
                {(holidaysByDate[selectedDateStr] || []).length > 0 && (
                  <Badge variant="outline" className="text-[10px] border-orange-500 text-orange-400">
                    {holidaysByDate[selectedDateStr][0].holiday_name}
                  </Badge>
                )}
              </div>

              {(myShiftsByDate[selectedDateStr] || []).length === 0 ? (
                <Card className="bg-gray-800/40 border-gray-700">
                  <CardContent className="p-8 text-center">
                    <CalendarDays className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No shifts scheduled for this day</p>
                    <p className="text-xs text-gray-500 mt-1">Enjoy your day off!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {(myShiftsByDate[selectedDateStr] || []).map((shift) => renderShiftCard(shift, false))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {rangeDays
                .filter((day) => calendarView !== "month" || isSameMonth(day, anchorDate))
                .map((day) => {
                  const dateStr = format(day, "yyyy-MM-dd");
                  const dayShifts = myShiftsByDate[dateStr] || [];
                  const today = isToday(day);

                  return (
                    <div key={dateStr}>
                      {renderHolidayBanner(dateStr)}
                      <div className="flex items-center gap-2 mb-2 mt-2">
                        <h3 className={`text-sm font-semibold ${today ? "text-blue-400" : "text-white"}`}>
                          {format(day, "EEEE")} {formatDate(day)}
                        </h3>
                        {today && (
                          <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-blue-600">
                            Today
                          </Badge>
                        )}
                      </div>

                      {dayShifts.length === 0 ? (
                        <Card className="bg-gray-800/30 border-gray-700/50">
                          <CardContent className="p-4 text-center">
                            <p className="text-xs text-gray-500">No shifts</p>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="space-y-2">
                          {dayShifts.map((shift) => renderShiftCard(shift, false))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="today-break" className="mt-4">
          <Card className="bg-gray-800/40 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Coffee className="h-4 w-4 text-amber-400" />
                Today's Break Schedule
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">{format(new Date(), "EEEE, d MMMM yyyy")}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {todaysShifts.length === 0 ? (
                <div className="text-center py-8">
                  <Coffee className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">
                    {inTodayRange
                      ? "No shift scheduled for you today."
                      : "Navigate to today's date to see your break schedule."}
                  </p>
                </div>
              ) : (
                todaysShifts.map((shift) => {
                  const breakInfo = scheduledBreaksByShiftId[shift.id];
                  const colour = getShiftColour(shift);

                  return (
                    <div key={shift.id} className="rounded-lg border border-gray-700 bg-gray-900/40 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-white">
                          {formatTime12(shift.start_hhmm)} – {formatTime12(shift.end_hhmm)}
                        </span>
                        <Badge
                          variant="outline"
                          className="text-[10px]"
                          style={{ borderColor: colour, color: colour }}
                        >
                          {shift.shift_type_label}
                        </Badge>
                      </div>

                      {breakInfo ? (
                        <div className="rounded-md border border-amber-700/40 bg-amber-950/20 px-3 py-2.5">
                          <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm">
                            <Coffee className="h-4 w-4" />
                            Break: {formatTime12(breakInfo.start)} – {formatTime12(breakInfo.end)}
                          </div>
                          <p className="text-xs text-amber-200/70 mt-1.5 leading-relaxed">
                            This time isn't random — it's worked out automatically by our break-scheduling
                            workflow: it targets roughly the middle of your shift so you're covered for the
                            whole day, lands on a clean 15-minute mark, and is checked against other drivers'
                            breaks so the fleet stays covered.
                          </p>
                        </div>
                      ) : (
                        <div className="rounded-md border border-gray-700 bg-gray-900/30 px-3 py-2.5 text-xs text-gray-400">
                          {shift.break_minutes > 0
                            ? `A ${shift.break_minutes}-minute break applies to this shift, but a specific time hasn't been scheduled yet.`
                            : "No break required for this shift."}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="full-schedule" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-400">
              All drivers' shifts for the selected period. Your shifts are highlighted.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowOtherDriverNames(!showOtherDriverNames)}
              className="text-gray-400 hover:text-white"
            >
              {showOtherDriverNames ? (
                <><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide Names</>
              ) : (
                <><Eye className="h-3.5 w-3.5 mr-1" /> Show Names</>
              )}
            </Button>
          </div>

          {renderCalendarStrip(fullScheduleByDate, true)}

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {rangeDays
              .filter((day) => calendarView !== "month" || isSameMonth(day, anchorDate))
              .map((day) => {
                const dateStr = format(day, "yyyy-MM-dd");
                const dayShifts = fullScheduleByDate[dateStr] || [];
                const today = isToday(day);
                const myDayShiftIds = new Set((myShiftsByDate[dateStr] || []).map((s) => s.id));

                return (
                  <Card
                    key={dateStr}
                    className={`border ${
                      (holidaysByDate[dateStr] || []).length > 0
                        ? "border-orange-500/40 bg-orange-950/10"
                        : today
                        ? "border-blue-500/50 bg-blue-950/10"
                        : "border-gray-700 bg-gray-800/40"
                    }`}
                  >
                    {renderHolidayBanner(dateStr)}
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className={`flex items-center gap-2 ${today ? "text-blue-400" : "text-white"}`}>
                          {format(day, "EEE")} {formatDate(day)}
                          {today && (
                            <Badge variant="default" className="text-[9px] px-1 py-0 bg-blue-600">
                              Today
                            </Badge>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">
                          {dayShifts.length} shift{dayShifts.length !== 1 ? "s" : ""}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0 space-y-1.5">
                      {dayShifts.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center py-4">No shifts</p>
                      ) : (
                        dayShifts.map((shift) => {
                          const isMe = myDayShiftIds.has(shift.id);
                          const colour = getShiftColour(shift);

                          return (
                            <div
                              key={shift.id}
                              className={`rounded-md px-2.5 py-2 transition-all ${
                                isMe
                                  ? "bg-blue-950/30 border border-blue-700/40"
                                  : "bg-gray-800/50 border border-gray-700/30"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0"
                                    style={{ backgroundColor: isMe ? "#3B82F6" : stringToColour(shift.driver_name) }}
                                  >
                                    {isMe ? "ME" : getInitials(shift)}
                                  </div>
                                  <div className="min-w-0">
                                    <p className={`text-xs font-medium truncate ${isMe ? "text-blue-300" : "text-gray-300"}`}>
                                      {isMe ? "You" : (showOtherDriverNames ? driverDisplayName(shift) : "Driver")}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                      {formatTime12(shift.start_hhmm)} – {formatTime12(shift.end_hhmm)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  {shift.vehicle_number && (
                                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                      {getVehicleTypeIcon(shift.vehicle_type, vehicleTypes)}
                                      {shift.vehicle_number}
                                    </span>
                                  )}
                                  <Badge
                                    variant="outline"
                                    className="text-[9px] px-1 py-0"
                                    style={{ borderColor: colour, color: colour }}
                                  >
                                    {shift.shift_type_code}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
            {rangeDays
              .filter((day) => calendarView !== "month" || isSameMonth(day, anchorDate))
              .map((day) => {
                const dateStr = format(day, "yyyy-MM-dd");
                const today = isToday(day);
                const isSelected = (selectedDay || format(anchorDate, "yyyy-MM-dd")) === dateStr;
                const dayShifts = fullScheduleByDate[dateStr] || [];

                return (
                  <button
                    key={dateStr}
                    onClick={() => setSelectedDay(dateStr)}
                    className={`shrink-0 rounded-lg border px-4 py-2 text-center transition-all ${
                      isSelected
                        ? "border-blue-500 bg-blue-950/40 ring-1 ring-blue-500/30"
                        : today
                        ? "border-blue-500/30 bg-gray-800/60 hover:bg-gray-800/80"
                        : "border-gray-700 bg-gray-800/40 hover:border-gray-600"
                    }`}
                  >
                    <p className={`text-xs font-medium ${isSelected ? "text-blue-400" : today ? "text-blue-400" : "text-gray-400"}`}>
                      {format(day, "EEE")}
                    </p>
                    <p className={`text-sm font-bold ${isSelected ? "text-white" : "text-gray-300"}`}>
                      {format(day, "d")}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {dayShifts.length} shift{dayShifts.length !== 1 ? "s" : ""}
                    </p>
                  </button>
                );
              })}
          </div>

          {(() => {
            const timelineDateStr = selectedDay || format(anchorDate, "yyyy-MM-dd");
            const timelineShifts = fullScheduleByDate[timelineDateStr] || [];

            if (timelineShifts.length === 0) {
              return (
                <Card className="bg-gray-800/40 border-gray-700">
                  <CardContent className="p-12 text-center">
                    <Clock3 className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No shifts scheduled for this day</p>
                  </CardContent>
                </Card>
              );
            }

            const driverMap = new Map<string, ShiftRow[]>();
            for (const s of timelineShifts) {
              const existing = driverMap.get(s.driver_name) || [];
              existing.push(s);
              driverMap.set(s.driver_name, existing);
            }

            interface TimelineDriverGroup {
              driverName: string;
              displayName: string;
              initials: string;
              avatarColour: string;
              totalHours: number;
              shifts: ShiftRow[];
              isMe: boolean;
            }

            const groups: TimelineDriverGroup[] = [];
            for (const [dn, driverShifts] of driverMap.entries()) {
              const first = driverShifts[0];
              const totalHours = driverShifts
                .filter((s) => s.status !== "cancelled")
                .reduce((sum, s) => sum + (s.worked_hours || 0), 0);

              groups.push({
                driverName: dn,
                displayName: driverDisplayName(first),
                initials: getInitials(first),
                avatarColour: dn === driverName ? "#3B82F6" : stringToColour(dn),
                totalHours,
                shifts: driverShifts.sort((a, b) => a.start_hhmm.localeCompare(b.start_hhmm)),
                isMe: dn === driverName,
              });
            }

            groups.sort((a, b) => {
              if (a.isMe && !b.isMe) return -1;
              if (!a.isMe && b.isMe) return 1;
              const aStart = a.shifts[0]?.start_hhmm || "99:99";
              const bStart = b.shifts[0]?.start_hhmm || "99:99";
              return aStart.localeCompare(bStart);
            });

            let currentY = 0;
            const driverRowData = groups.map((group) => {
              const rowCount = group.shifts.length || 1;
              const groupHeight = rowCount * (ROW_HEIGHT + ROW_GAP);
              const startY = currentY;
              currentY += groupHeight;
              return { ...group, startY, groupHeight };
            });

            const totalHeight = driverRowData.reduce((sum, d) => sum + d.groupHeight, 0);
            const now = new Date();
            const todayStr = format(now, "yyyy-MM-dd");
            const nowX = todayStr === timelineDateStr ? (now.getHours() + now.getMinutes() / 60) * HOUR_WIDTH : null;

            const TimelineShiftBar: React.FC<{ shift: ShiftRow }> = ({ shift }) => {
              const colour = getShiftColour(shift);
              const startH = timeToHours(shift.start_hhmm);
              const endH = timeToHours(shift.end_hhmm);
              const isCancelled = shift.status === "cancelled";
              const isMe = shift.driver_name === driverName;

              let durationH = endH - startH;
              if (durationH <= 0) durationH += 24;

              const leftPx = startH * HOUR_WIDTH;
              const widthPx = Math.max(durationH * HOUR_WIDTH, 60);

              return (
                <div
                  className={`absolute top-1 select-none transition-all ${
                    isCancelled ? "opacity-30" : isMe ? "hover:brightness-110 hover:shadow-lg hover:z-20" : "opacity-60 hover:opacity-80"
                  }`}
                  style={{
                    left: `${leftPx}px`,
                    width: `${widthPx}px`,
                    height: `${ROW_HEIGHT - 8}px`,
                    borderRadius: "6px",
                    backgroundColor: isMe ? `${colour}CC` : `${colour}55`,
                    border: isMe ? `2px solid ${colour}` : `1px solid ${colour}44`,
                    zIndex: isMe ? 15 : 10,
                  }}
                >
                  <div className="px-2 py-1 h-full flex flex-col justify-center overflow-hidden">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold truncate" style={{ color: isMe ? "#fff" : `${colour}DD` }}>
                        {formatTime12(shift.start_hhmm)} - {formatTime12(shift.end_hhmm)}
                      </span>
                      <span className="text-[10px] whitespace-nowrap" style={{ color: isMe ? "rgba(255,255,255,0.7)" : `${colour}99` }}>
                        {formatDuration(shift.worked_hours)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      {shift.vehicle_number && (
                        getIconStyle(shift.vehicle_type, vehicleTypes) === "truck" ? (
                          <Truck className="h-3 w-3 shrink-0" style={{ color: isMe ? "rgba(255,255,255,0.6)" : `${colour}77` }} />
                        ) : (
                          <Car className="h-3 w-3 shrink-0" style={{ color: isMe ? "rgba(255,255,255,0.6)" : `${colour}77` }} />
                        )
                      )}
                      <span className="text-[10px] truncate" style={{ color: isMe ? "rgba(255,255,255,0.7)" : `${colour}99` }}>
                        {isMe ? shift.shift_type_label : `${driverDisplayName(shift)} • ${shift.shift_type_label}`}
                        {shift.vehicle_number ? ` • ${shift.vehicle_number}` : ""}
                      </span>
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <div>
                {renderHolidayBanner(timelineDateStr)}
                <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-900/60">
                  <div className="flex">
                    <div className="shrink-0 border-r border-gray-700 bg-gray-900/80 z-20" style={{ width: DRIVER_COL_WIDTH }}>
                      <div className="border-b border-gray-700 flex items-center justify-center text-xs text-gray-500 font-medium" style={{ height: HEADER_HEIGHT }}>
                        {format(new Date(timelineDateStr + "T00:00:00"), "EEEE d MMM")}
                      </div>
                      <div style={{ height: totalHeight }}>
                        {driverRowData.map((group) => (
                          <div
                            key={group.driverName}
                            className={`flex items-center gap-2.5 px-3 border-b ${
                              group.isMe ? "border-blue-800/40 bg-blue-950/20" : "border-gray-800/60"
                            }`}
                            style={{ height: group.groupHeight }}
                          >
                            <div
                              className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                              style={{ backgroundColor: group.avatarColour }}
                            >
                              {group.isMe ? "ME" : group.initials}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm font-semibold truncate leading-tight ${group.isMe ? "text-blue-300" : "text-white"}`}>
                                {group.isMe ? "You" : (showOtherDriverNames ? group.displayName : "Driver")}
                              </p>
                              <p className="text-xs text-gray-400 leading-tight">
                                {formatDuration(group.totalHours)}
                              </p>
                            </div>
                            {group.isMe && (
                              <Badge variant="default" className="text-[9px] px-1 py-0 bg-blue-600 shrink-0">
                                You
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex-1 overflow-x-auto">
                      <div style={{ width: TIMELINE_WIDTH, minWidth: "100%" }}>
                        <div className="flex border-b border-gray-700 sticky top-0 bg-gray-900/95 z-10" style={{ height: HEADER_HEIGHT }}>
                          {HOURS.map((h) => (
                            <div
                              key={h}
                              className="shrink-0 flex items-center justify-center text-xs text-gray-400 font-medium border-r border-gray-800/40"
                              style={{ width: HOUR_WIDTH }}
                            >
                              {formatHour(h)}
                            </div>
                          ))}
                        </div>

                        <div className="relative" style={{ height: totalHeight }}>
                          {HOURS.map((h) => (
                            <React.Fragment key={h}>
                              <div className="absolute top-0 bottom-0 border-r border-gray-800/30" style={{ left: h * HOUR_WIDTH }} />
                              <div className="absolute top-0 bottom-0 border-r border-gray-800/15" style={{ left: h * HOUR_WIDTH + HOUR_WIDTH / 2 }} />
                            </React.Fragment>
                          ))}

                          {nowX !== null && (
                            <div className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{ left: nowX }}>
                              <div className="w-0.5 h-full bg-red-500/70" />
                              <div className="absolute -top-1 -left-1.5 w-3 h-3 rounded-full bg-red-500" style={{ top: -2 }} />
                            </div>
                          )}

                          {driverRowData.map((group) => (
                            <div key={group.driverName}>
                              <div
                                className={`absolute left-0 right-0 border-b ${
                                  group.isMe ? "border-blue-800/30 bg-blue-950/10" : "border-gray-800/40"
                                }`}
                                style={{ top: group.startY, height: group.groupHeight }}
                              />
                              {group.shifts.map((shift, idx) => {
                                const rowY = group.startY + idx * (ROW_HEIGHT + ROW_GAP);
                                return (
                                  <div
                                    key={shift.id}
                                    className="absolute"
                                    style={{ top: rowY, left: 0, width: TIMELINE_WIDTH, height: ROW_HEIGHT }}
                                  >
                                    <TimelineShiftBar shift={shift} />
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="swaps" className="mt-4">
          <div className="grid grid-cols-1 xl:grid-cols-[430px_1fr] gap-4">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <PlusCircle className="h-4 w-4 text-purple-400" />
                  Create Swap / Release Request
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSwapMode("open_bid")}
                    className={`px-3 py-2 rounded-md text-sm border transition-colors ${
                      swapMode === "open_bid"
                        ? "bg-purple-600 border-purple-600 text-white"
                        : "bg-gray-900 border-gray-700 text-gray-300"
                    }`}
                  >
                    Open for Bids
                  </button>
                  <button
                    type="button"
                    onClick={() => setSwapMode("direct_swap")}
                    className={`px-3 py-2 rounded-md text-sm border transition-colors ${
                      swapMode === "direct_swap"
                        ? "bg-purple-600 border-purple-600 text-white"
                        : "bg-gray-900 border-gray-700 text-gray-300"
                    }`}
                  >
                    Direct Swap
                  </button>
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Your shift</label>
                  <select
                    value={selectedOwnShiftId}
                    onChange={(e) => {
                      setSelectedOwnShiftId(e.target.value);
                      setSelectedTargetShiftId("");
                    }}
                    className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white"
                  >
                    <option value="">Select your shift</option>
                    {ownShiftOptions.map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {format(new Date(`${shift.shift_date}T00:00:00`), "EEE d MMM")} · {shift.start_hhmm}-{shift.end_hhmm} · {shift.shift_type_label}
                      </option>
                    ))}
                  </select>
                </div>

                {swapMode === "direct_swap" && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Target shift</label>
                    <select
                      value={selectedTargetShiftId}
                      onChange={(e) => setSelectedTargetShiftId(e.target.value)}
                      className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white"
                    >
                      <option value="">Select another driver's shift</option>
                      {targetShiftOptions.map((shift) => (
                        <option key={shift.id} value={shift.id}>
                          {driverDisplayName(shift)} · {format(new Date(`${shift.shift_date}T00:00:00`), "EEE d MMM")} · {shift.start_hhmm}-{shift.end_hhmm}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Reason</label>
                  <Textarea
                    value={swapReason}
                    onChange={(e) => setSwapReason(e.target.value)}
                    placeholder="Add a reason..."
                    className="bg-gray-900 border-gray-700 text-white min-h-[96px]"
                  />
                </div>

                <Button
                  onClick={createSwapRequest}
                  disabled={
                    !selectedOwnShiftId ||
                    creatingSwap ||
                    (swapMode === "direct_swap" && !selectedTargetShiftId)
                  }
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {creatingSwap ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card className="bg-gray-800/40 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Gavel className="h-4 w-4 text-amber-400" />
                    Open Requests You Can Bid On
                    {openBidCount > 0 && (
                      <Badge className="bg-amber-600 text-white">{openBidCount}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {openBidRequests.length === 0 ? (
                    <p className="text-sm text-gray-500">No open bid requests right now.</p>
                  ) : (
                    openBidRequests.map((req) => {
                      const sourceShift = swapShiftMap[req.shift_id];
                      const myExistingBid = myBidsByRequestId.get(req.id);
                      const hasBid = Boolean(myExistingBid);

                      return (
                        <div key={req.id} className="rounded-lg border border-gray-700 bg-gray-900/40 p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-medium text-white">
                                {req.requesting_driver}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                Requested {format(new Date(req.requested_at), "d MMM yyyy 'at' h:mm a")}
                              </div>
                            </div>
                            {hasBid ? (
                              <Badge variant="outline" className="border-cyan-500 text-cyan-400 text-[10px]">
                                Bid placed
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-amber-500 text-amber-400 text-[10px]">
                                Open
                              </Badge>
                            )}
                          </div>

                          <div className="rounded-md border border-gray-700 bg-gray-950/40 p-3 text-sm text-white">
                            {formatShiftSummary(sourceShift)}
                          </div>

                          {req.reason && (
                            <div className="text-xs text-gray-300">
                              <span className="text-gray-500">Reason:</span> {req.reason}
                            </div>
                          )}

                          {hasBid ? (
                            <div className="text-xs text-gray-400">
                              Your bid is <span className="text-white">{myExistingBid?.status}</span>.
                              {myExistingBid?.note && (
                                <span> Note: {myExistingBid.note}</span>
                              )}
                            </div>
                          ) : (
                            <>
                              <Textarea
                                value={bidNotes[req.id] || ""}
                                onChange={(e) =>
                                  setBidNotes((prev) => ({ ...prev, [req.id]: e.target.value }))
                                }
                                placeholder="Optional note for admin..."
                                className="bg-gray-900 border-gray-700 text-white min-h-[76px]"
                              />
                              <Button
                                onClick={() => placeBid(req.id)}
                                disabled={placingBidId === req.id}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                              >
                                {placingBidId === req.id ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Placing bid...
                                  </>
                                ) : (
                                  <>
                                    <Gavel className="h-4 w-4 mr-2" />
                                    Bid for This Shift
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gray-800/40 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <ArrowLeftRight className="h-4 w-4 text-purple-400" />
                    My Requests
                    {pendingMyRequestsCount > 0 && (
                      <Badge className="bg-purple-600 text-white">{pendingMyRequestsCount} pending</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {swapRequests.length === 0 ? (
                    <p className="text-sm text-gray-500">You haven’t created any swap requests yet.</p>
                  ) : (
                    swapRequests.map((req) => {
                      const sourceShift = swapShiftMap[req.shift_id];
                      const targetShift = req.target_shift_id ? swapShiftMap[req.target_shift_id] : null;

                      return (
                        <div key={req.id} className="rounded-lg border border-gray-700 bg-gray-900/40 p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-medium text-white">
                                {req.is_open_bid ? "Open for bids" : "Direct swap"}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                Requested {format(new Date(req.requested_at), "d MMM yyyy 'at' h:mm a")}
                              </div>
                            </div>
                            {renderSwapStatusBadge(req.status)}
                          </div>

                          <div className="rounded-md border border-gray-700 bg-gray-950/40 p-3 text-sm text-white">
                            {formatShiftSummary(sourceShift)}
                          </div>

                          {targetShift && (
                            <div className="rounded-md border border-gray-700 bg-gray-950/40 p-3 text-sm text-white">
                              Target: {formatShiftSummary(targetShift)}
                            </div>
                          )}

                          {req.reason && (
                            <div className="text-xs text-gray-300">
                              <span className="text-gray-500">Reason:</span> {req.reason}
                            </div>
                          )}

                          {req.admin_notes && (
                            <div className="text-xs text-gray-300">
                              <span className="text-gray-500">Admin notes:</span> {req.admin_notes}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gray-800/40 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Send className="h-4 w-4 text-cyan-400" />
                    My Bids
                    {pendingMyBidsCount > 0 && (
                      <Badge className="bg-cyan-600 text-white">{pendingMyBidsCount} pending</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {myBids.length === 0 ? (
                    <p className="text-sm text-gray-500">You haven’t placed any bids yet.</p>
                  ) : (
                    myBids.map((bid) => {
                      const request = openBidRequests.find((r) => r.id === bid.swap_request_id)
                        || swapRequests.find((r) => r.id === bid.swap_request_id);
                      const sourceShift = request ? swapShiftMap[request.shift_id] : null;

                      return (
                        <div key={bid.id} className="rounded-lg border border-gray-700 bg-gray-900/40 p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-medium text-white">
                                Bid on {request?.requesting_driver || "request"}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                Placed {format(new Date(bid.created_at), "d MMM yyyy 'at' h:mm a")}
                              </div>
                            </div>

                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                bid.status === "accepted"
                                  ? "border-green-500 text-green-400"
                                  : bid.status === "rejected"
                                  ? "border-red-500 text-red-400"
                                  : bid.status === "withdrawn"
                                  ? "border-gray-500 text-gray-400"
                                  : "border-amber-500 text-amber-400"
                              }`}
                            >
                              {bid.status}
                            </Badge>
                          </div>

                          <div className="rounded-md border border-gray-700 bg-gray-950/40 p-3 text-sm text-white">
                            {formatShiftSummary(sourceShift)}
                          </div>

                          {bid.note && (
                            <div className="text-xs text-gray-300">
                              <span className="text-gray-500">Your note:</span> {bid.note}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="time-off" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <CalendarOff className="h-4 w-4 text-orange-400" />
                Time Off
              </h3>
              <p className="text-xs text-gray-500 mt-1">Request leave and track approvals without it living at the bottom like an afterthought.</p>
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setTimeOffDefaultDate(selectedDay || format(anchorDate, "yyyy-MM-dd"));
                setTimeOffDialogOpen(true);
              }}
              className="border-orange-700/50 text-orange-400 hover:bg-orange-900/20"
            >
              <CalendarOff className="h-4 w-4 mr-1" /> Request Time Off
            </Button>
          </div>

          {timeOffRequests.length === 0 ? (
            <Card className="bg-gray-800/30 border-gray-700">
              <CardContent className="p-8 text-center">
                <CalendarOff className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No time off requests yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {timeOffRequests.map((req) => (
                <div
                  key={req.id}
                  className={`rounded-lg border p-3 ${
                    req.status === "pending"
                      ? "border-amber-700/50 bg-amber-950/10 border-dashed"
                      : req.status === "approved"
                      ? "border-green-700/50 bg-green-950/10"
                      : "border-red-700/50 bg-red-950/10"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CalendarOff className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm text-white font-medium">
                          {req.request_type === "partial_day" && req.start_time && req.end_time
                            ? `${formatTime12(req.start_time)} - ${formatTime12(req.end_time)}`
                            : req.request_type === "multi_day"
                            ? `${formatDate(new Date(`${req.start_date}T00:00:00`))} - ${formatDate(new Date(`${req.end_date}T00:00:00`))}`
                            : `${format(new Date(`${req.start_date}T00:00:00`), "EEEE")}, ${formatDate(new Date(`${req.start_date}T00:00:00`))}`}
                        </p>
                        <p className="text-xs text-gray-400">{req.reason}</p>
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${
                        req.status === "pending"
                          ? "border-amber-500 text-amber-400"
                          : req.status === "approved"
                          ? "border-green-500 text-green-400"
                          : "border-red-500 text-red-400"
                      }`}
                    >
                      {req.status}
                    </Badge>
                  </div>
                  {req.admin_notes && (
                    <p className="text-xs text-gray-500 mt-1">Admin: {req.admin_notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TimeOffRequestDialog
        open={timeOffDialogOpen}
        onOpenChange={setTimeOffDialogOpen}
        driverName={driverName}
        defaultDate={timeOffDefaultDate}
        onCreated={loadRoster}
      />
    </div>
  );
};

export default DriverRosterView;